/**
 * Action builders for meeting media controls.
 */
import {
  setAudioEnabled,
  setVideoEnabled,
  startScreenShare as livekitStartShare,
  stopScreenShare as livekitStopShare,
  switchCamera,
} from '../services/livekit';
import { updateParticipantSharing } from './meetingStoreParticipants';
import type { MeetingStoreGet, MeetingStoreSet } from './meetingStoreTypes';

/**
 * Builds the toggle mute action.
 */
export function toggleMuteAction(set: MeetingStoreSet, get: MeetingStoreGet) {
  return (): void => {
    const { localParticipant } = get();
    if (!localParticipant) return;

    const newMuted = !localParticipant.isMuted;
    setAudioEnabled(!newMuted);
    set({ localParticipant: { ...localParticipant, isMuted: newMuted } });
  };
}

/**
 * Builds the toggle camera action.
 */
export function toggleCameraAction(set: MeetingStoreSet, get: MeetingStoreGet) {
  return (): void => {
    const { localParticipant } = get();
    if (!localParticipant) return;

    const newCameraOn = !localParticipant.isCameraOn;
    setVideoEnabled(newCameraOn);
    set({
      localParticipant: {
        ...localParticipant,
        isCameraOn: newCameraOn,
        videoTrack: newCameraOn ? localParticipant.videoTrack : undefined,
      },
    });
  };
}

/**
 * Builds the flip camera action.
 */
export function flipCameraAction(set: MeetingStoreSet, get: MeetingStoreGet) {
  return async (): Promise<void> => {
    const { cameraDevices, currentCameraIndex } = get();
    if (cameraDevices.length < 2) return;

    const nextIndex = (currentCameraIndex + 1) % cameraDevices.length;
    const nextDevice = cameraDevices[nextIndex];

    try {
      await switchCamera(nextDevice.deviceId);
      set({ currentCameraIndex: nextIndex });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to switch';
      set({ error: message });
    }
  };
}

/**
 * Builds the start screen share action.
 */
export function startScreenShareAction(set: MeetingStoreSet) {
  return async (): Promise<void> => {
    try {
      await livekitStartShare();
      set((state) => {
        const localParticipant = state.localParticipant;
        const localId = localParticipant?.participantId ?? null;

        return {
          isSharingScreen: true,
          screenShareOwnerId: localId,
          participants: localId
            ? updateParticipantSharing(state.participants, localId, true)
            : state.participants,
          localParticipant: localParticipant
            ? { ...localParticipant, isSharingScreen: true }
            : localParticipant,
        };
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to share';
      set({ error: message });
    }
  };
}

/**
 * Builds the stop screen share action.
 */
export function stopScreenShareAction(set: MeetingStoreSet) {
  return async (): Promise<void> => {
    await livekitStopShare();
    set((state) => ({
      isSharingScreen: false,
      screenShareOwnerId: null,
      screenShareTrack: null,
      participants: state.screenShareOwnerId
        ? updateParticipantSharing(state.participants, state.screenShareOwnerId, false)
        : state.participants,
      localParticipant: state.localParticipant
        ? { ...state.localParticipant, isSharingScreen: false }
        : state.localParticipant,
    }));
  };
}

/**
 * Builds the clear error action.
 */
export function clearErrorAction(set: MeetingStoreSet) {
  return (): void => {
    set({ error: null });
  };
}
